import React, { useEffect, useState } from 'react'
import {
  PixelPageContainer,
  Section,
  SectionContent,
  DcText,
  DcFlexBox,
} from 'pixel-base'
import { AjaxResponse } from 'rxjs/ajax'
import styled from 'styled-components'

import { fetchProductHistory } from 'utils/api-routes.util'
import InventoryHistoryTable from './InventoryHistoryTable'

interface IWarehouseInventory {
  product_id: number
  product_name: string
  sku: string
  warehouse_id: number
  notes: string
  created_at: {
    date: string
  }
  unavailable: number | null
  committed: number | null
  available: number | null
  on_hand: number | null
  quantity: number | null
  reason: string | null
  is_current?: number | null
}

interface ProductInfo {
  product_id: number
  product_name: string
  product_sku: string
  product_image_path: string
  is_parent: boolean
  variation_type: string | null
  variation_value: string | null
  linked_stores?: Array<{
    store_id: string
    store_name: string
  }>
}

interface InventoryHistoryResponse {
  data: Array<{
    variation_info: {
      product_id: string
      product_name: string
      product_sku: string
      product_image_path: string
      variation_type: string | null
      variation_value: string | null
      is_variation: boolean
    }
    history: IWarehouseInventory[]
  }>
  _meta: {
    total: number
    page: number
    page_size: number
    pages: number
  }
  product_info: ProductInfo
}

interface InventoryItemsProps {
  data: {
    product_id: number
    warehouse_id: number
  }
}

// Styled components for store display
const StoreChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  margin-top: 8px;
`

const StoreChip = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: #e3f2fd;
  color: #1976d2;
  border: 1px solid #bbdefb;
  white-space: nowrap;
`

const NoLinksMessage = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  white-space: nowrap;
`

const InventoryItems: React.FC<InventoryItemsProps> = ({ data }) => {
  const [inventoryItems, setInventoryItems] = React.useState<
    IWarehouseInventory[]
  >([])
  const [productInfo, setProductInfo] = React.useState<ProductInfo | null>(null)
  const [tableLoading, setTableLoader] = React.useState(false)
  const [orderId, setOrderId] = React.useState<string>('')
  const [showOrderDetailModal, setShowOrderDetailModal] = React.useState(false)
  const currentRequestRef = React.useRef<number | null>(null)

  // Local pagination state
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize] = useState(20)
  const [paginatedData, setPaginatedData] = useState<IWarehouseInventory[]>([])

  // Sorting state
  const [sortBy, setSortBy] = useState<'on_hand' | 'committed' | null>(null)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const fetchInventoryItems = (
    data: InventoryItemsProps['data'],
    sortBy?: string,
    sortOrder?: string,
  ) => {
    // Cancel previous request if it exists
    if (currentRequestRef.current !== null) {
      currentRequestRef.current = null
    }

    // Generate a unique request ID
    const requestId = Date.now()
    currentRequestRef.current = requestId

    setTableLoader(true)
    fetchProductHistory(
      data.product_id,
      data.warehouse_id,
      sortBy,
      sortOrder,
    ).subscribe({
      next: (res) => {
        // Only process if this is still the current request
        if (currentRequestRef.current !== requestId) {
          return
        }

        const typedRes = res as AjaxResponse<InventoryHistoryResponse>
        const responseData = typedRes.response

        // Extract product info
        if (responseData?.product_info) {
          setProductInfo(responseData.product_info)
        }

        // Extract history data from the first variation (or all variations)
        if (responseData?.data && responseData.data.length > 0) {
          // Flatten all history from all variations
          const allHistory = responseData.data.flatMap(
            (variation) => variation.history || [],
          )
          setInventoryItems(allHistory)
          // Reset pagination when new data is loaded
          setCurrentPage(0)
        } else {
          setInventoryItems([])
          setPaginatedData([])
        }

        setTableLoader(false)
      },
      error: (err) => {
        // Only process if this is still the current request
        if (currentRequestRef.current !== requestId) {
          return
        }

        console.error('Error fetching inventory items:', err)
        setInventoryItems([])
        setProductInfo(null)
        setTableLoader(false)
      },
    })
  }

  // Handle sorting
  const handleSort = (column: 'on_hand' | 'committed') => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new column and default to asc
      setSortBy(column)
      setSortOrder('asc')
    }
  }

  useEffect(() => {
    if (!data.product_id) return
    fetchInventoryItems(data, sortBy || undefined, sortOrder)
  }, [data, sortBy, sortOrder])

  // Handle local pagination
  useEffect(() => {
    const startIndex = currentPage * pageSize
    const endIndex = startIndex + pageSize
    const paginated = inventoryItems.slice(startIndex, endIndex)
    setPaginatedData(paginated)
  }, [inventoryItems, currentPage, pageSize])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      currentRequestRef.current = null
    }
  }, [])

  return (
    <>
      <PixelPageContainer
        style={{
          padding: '0px',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Section
          style={{
            padding: '20px',
            flex: '0 0 auto',
          }}
        >
          <SectionContent>
            {/* Product Information Header */}
            {productInfo && (
              <DcFlexBox
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                }}
                alignItems="flex-start"
                justifyContent="space-between"
                gap="20px"
              >
                <ProductImage
                  src={productInfo.product_image_path}
                  alt={productInfo.product_name}
                  style={{
                    width: '120px',
                    height: '120px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                />
                <DcFlexBox style={{ flex: 1 }} flexDirection="column" gap="8px">
                  <DcText fontSize="24px" fontWeight="600" color="#2c3e50">
                    {productInfo.product_name}
                  </DcText>
                  <DcText fontSize="16px" color="#6c757d">
                    SKU: {productInfo.product_sku}
                  </DcText>
                  <DcText fontSize="14px" color="#6c757d">
                    Product ID: {productInfo.product_id}
                  </DcText>

                  {/* Linked Stores Section */}
                  <StoreChipsContainer>
                    {productInfo.linked_stores &&
                    productInfo.linked_stores.length > 0 ? (
                      productInfo.linked_stores.map((store, index) => (
                        <StoreChip key={`${store.store_id}-${index}`}>
                          {store.store_name}
                        </StoreChip>
                      ))
                    ) : (
                      <NoLinksMessage>Link Missing</NoLinksMessage>
                    )}
                  </StoreChipsContainer>
                </DcFlexBox>
              </DcFlexBox>
            )}
          </SectionContent>
        </Section>

        {/* Scrollable Table Section */}
        <Section
          style={{
            padding: '0 20px 20px 20px',
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0, // Important for flexbox scrolling
          }}
        >
          <SectionContent
            style={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                // flex: '1 1 auto',
                overflow: 'auto',
                minHeight: 0,
              }}
            >
              <InventoryHistoryTable
                data={paginatedData || []}
                loading={tableLoading}
                onOrderClick={(orderId) => {
                  setOrderId(orderId)
                  setShowOrderDetailModal(true)
                }}
                onSort={handleSort}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            </div>

            {/* Pagination Controls */}
            {inventoryItems.length > pageSize && (
              <DcFlexBox
                justifyContent="space-between"
                alignItems="center"
                height="auto"
                style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  flex: '0 0 auto',
                }}
              >
                <DcText fontSize="14px" color="#6c757d">
                  Showing {currentPage * pageSize + 1} to{' '}
                  {Math.min(
                    (currentPage + 1) * pageSize,
                    inventoryItems.length,
                  )}{' '}
                  of {inventoryItems.length} entries
                </DcText>
                <DcFlexBox width="auto" height="auto" gap="8px">
                  <button
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      backgroundColor: currentPage === 0 ? '#f8f9fa' : 'white',
                      color: currentPage === 0 ? '#6c757d' : '#495057',
                      cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage(
                        Math.min(
                          Math.ceil(inventoryItems.length / pageSize) - 1,
                          currentPage + 1,
                        ),
                      )
                    }
                    disabled={
                      currentPage >=
                      Math.ceil(inventoryItems.length / pageSize) - 1
                    }
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      backgroundColor:
                        currentPage >=
                        Math.ceil(inventoryItems.length / pageSize) - 1
                          ? '#f8f9fa'
                          : 'white',
                      color:
                        currentPage >=
                        Math.ceil(inventoryItems.length / pageSize) - 1
                          ? '#6c757d'
                          : '#495057',
                      cursor:
                        currentPage >=
                        Math.ceil(inventoryItems.length / pageSize) - 1
                          ? 'not-allowed'
                          : 'pointer',
                    }}
                  >
                    Next
                  </button>
                </DcFlexBox>
              </DcFlexBox>
            )}
          </SectionContent>
        </Section>
      </PixelPageContainer>
    </>
  )
}

const ProductImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

export default InventoryItems
